import { lazy, Suspense, useState } from 'react';
import MessageDialog, {
  MessageDialogInterfaceProps,
} from '../message/MessageDialog';
import NoteDialog, { NoteDialogInterfaceProps } from '../NoteDialog';
import PhoneCallDialog, {
  PhoneCallDialogInterfaceProps,
} from '../PhoneCallDialog';
import ScheduledEventDialog, {
  ScheduledEventDialogInterfaceProps,
} from '../ScheduledEventDialog';
import VisitDialog, { VisitDialogInterfaceProps } from '../VisitDialog';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';

/**
 * Lazy-loaded so this module does not synchronously import the calls fullscreen graph
 * (Places, maps loader, etc.). A static import can duplicate this file in the bundle.
 */
const CallsFullScreenContentLazy = lazy(() =>
  import('@act/react-components/member-page/Calls/CallsFullScreenContent').then(
    m => ({ default: m.CallsFullScreenContent })
  )
);

export interface DialogProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

export type InteractionsDialogService = {
  handleDialogClose: () => void;
  createScheduledEvent: (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => void;
  editScheduledEvent: (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => void;
  createVisit: (interfaceProps: VisitDialogInterfaceProps) => void;
  editVisit: (interfaceProps: VisitDialogInterfaceProps) => void;
  createNote: (interfaceProps: NoteDialogInterfaceProps) => void;
  editNote: (interfaceProps: NoteDialogInterfaceProps) => void;
  createMessage: (interfaceProps: MessageDialogInterfaceProps) => void;
  editMessage: (interfaceProps: MessageDialogInterfaceProps) => void;
  createCall: (interfaceProps: PhoneCallDialogInterfaceProps) => void;
  editCall: (interfaceProps: PhoneCallDialogInterfaceProps) => void;
};

/** Shared across possible duplicate module instances (HMR, split chunks). */
const INTERACTIONS_DIALOG_REGISTRY_KEY = Symbol.for(
  'act.care-portal.interactions-dialog-service'
);

type InteractionsDialogRegistry = {
  service: InteractionsDialogService | null;
};

/**
 * @returns Process-wide registry for the interactions dialog service.
 */
function getInteractionsDialogRegistry(): InteractionsDialogRegistry {
  const root = globalThis as typeof globalThis & {
    [INTERACTIONS_DIALOG_REGISTRY_KEY]?: InteractionsDialogRegistry;
  };
  if (!root[INTERACTIONS_DIALOG_REGISTRY_KEY]) {
    root[INTERACTIONS_DIALOG_REGISTRY_KEY] = { service: null };
  }
  return root[INTERACTIONS_DIALOG_REGISTRY_KEY];
}

const getRawService = (): InteractionsDialogService => {
  const service = getInteractionsDialogRegistry().service;
  if (!service) {
    throw new Error(
      `Cannot use useInteractionsDialog without including <InteractionsDialogRoot /> somewhere on the dom tree`
    );
  }

  return service;
};

/** Delegates to {@link getInteractionsDialogRegistry} so callers work before the root mounts. */
const lazyGlobalDialogService: InteractionsDialogService = {
  handleDialogClose: () => getRawService().handleDialogClose(),
  createScheduledEvent: (interfaceProps: ScheduledEventDialogInterfaceProps) =>
    getRawService().createScheduledEvent(interfaceProps),
  editScheduledEvent: (interfaceProps: ScheduledEventDialogInterfaceProps) =>
    getRawService().editScheduledEvent(interfaceProps),
  createVisit: (interfaceProps: VisitDialogInterfaceProps) =>
    getRawService().createVisit(interfaceProps),
  editVisit: (interfaceProps: VisitDialogInterfaceProps) =>
    getRawService().editVisit(interfaceProps),
  createNote: (interfaceProps: NoteDialogInterfaceProps) =>
    getRawService().createNote(interfaceProps),
  editNote: (interfaceProps: NoteDialogInterfaceProps) =>
    getRawService().editNote(interfaceProps),
  createMessage: (interfaceProps: MessageDialogInterfaceProps) =>
    getRawService().createMessage(interfaceProps),
  editMessage: (interfaceProps: MessageDialogInterfaceProps) =>
    getRawService().editMessage(interfaceProps),
  createCall: (interfaceProps: PhoneCallDialogInterfaceProps) =>
    getRawService().createCall(interfaceProps),
  editCall: (interfaceProps: PhoneCallDialogInterfaceProps) =>
    getRawService().editCall(interfaceProps),
};

export const InteractionsDialogRoot = () => {
  const [dialogOpen, setDialogOpen] = useState<
    | 'None'
    | 'Scheduled Event'
    | 'Note'
    | 'Visit'
    | 'Text Message'
    | 'Phone Call'
    | 'Hospitalization'
  >('None');

  const [interfaceProps, setInterfaceProps] = useState(null);
  const [mode, setMode] = useState<'Create' | 'Edit'>('Edit');
  const useNewCallsDrawer = useFeatureFlag('needs-call');
  const resetState = () => {
    setMode('Edit');
    setInterfaceProps(null);
  };
  const handleDialogClose = () => {
    resetState();
    setDialogOpen('None');
  };

  const createScheduledEvent = (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Scheduled Event');
  };
  const editScheduledEvent = (
    interfaceProps: ScheduledEventDialogInterfaceProps
  ) => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Scheduled Event');
  };

  const createVisit = (interfaceProps: VisitDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Visit');
  };
  const editVisit = interfaceProps => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Visit');
  };
  const createNote = (interfaceProps: NoteDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Note');
  };
  const editNote = (interfaceProps: NoteDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Note');
  };
  const createMessage = (interfaceProps: MessageDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Text Message');
  };
  const editMessage = (interfaceProps: MessageDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Text Message');
  };
  const createCall = (interfaceProps: PhoneCallDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Create');
    setDialogOpen('Phone Call');
  };
  const editCall = (interfaceProps: PhoneCallDialogInterfaceProps) => {
    setInterfaceProps(interfaceProps);
    setMode('Edit');
    setDialogOpen('Phone Call');
  };

  getInteractionsDialogRegistry().service = {
    handleDialogClose,
    createScheduledEvent,
    editScheduledEvent,
    createVisit,
    editVisit,
    createNote,
    editNote,
    createMessage,
    editMessage,
    createCall,
    editCall,
  };

  const dialogProps = {
    handleClose: handleDialogClose,
    mode: mode,
    open: true,
    ...interfaceProps,
  };

  return (
    <>
      {dialogOpen === 'Scheduled Event' && (
        <ScheduledEventDialog {...dialogProps} />
      )}
      {dialogOpen === 'Note' && <NoteDialog {...dialogProps} />}
      {dialogOpen === 'Visit' && <VisitDialog {...dialogProps} />}
      {dialogOpen === 'Text Message' && <MessageDialog {...dialogProps} />}

      {dialogOpen === 'Phone Call' &&
        (useNewCallsDrawer ? (
          <Suspense fallback={null}>
            <CallsFullScreenContentLazy {...dialogProps} />
          </Suspense>
        ) : (
          <PhoneCallDialog {...dialogProps} />
        ))}
    </>
  );
};

export const useInteractionsDialog = () => {
  return lazyGlobalDialogService;
};

export default useInteractionsDialog;

export const reactFeedDialogService = lazyGlobalDialogService;
